// Core packages
import { Component, Inject, OnInit } from "@angular/core";

// Third party packages
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";

// Custom packages
import { SameCodeChoice, sameCodeLockMessage } from "../../same-code-docs-edit.utils";

/**
 * Script start
 */

/** What the property documents page passes to the dialog */
export interface SameCodeEditDialogData {
  /** `data.barCode` of the row the edit was opened from */
  barCode: string;
  /** Rows sharing that code, see `buildSameCodeChoices` */
  choices: SameCodeChoice[];
}

/** What the dialog closes with when the user continues; it closes with nothing otherwise */
export interface SameCodeEditDialogResult {
  selected: SameCodeChoice[];
}

/**
 * "Modifica dati aggiuntivi" on a document whose code other rows share: the user ticks the rows that
 * receive the edit, then the edit form opens. Nothing is ticked when the dialog opens. A row whose
 * data the form would not show cannot be ticked, and says why under its title.
 *
 * @since 3.0.0
 */
@Component({
  selector: "app-property-same-code-edit-dialog",
  templateUrl: "./document-same-code-edit-dialog.component.html",
  styleUrls: ["./document-same-code-edit-dialog.component.scss"],
})
export class PropertySameCodeEditDialogComponent implements OnInit {
  /** Last 7 characters of the code, as the delete dialog prints it */
  barCode = "";
  choices: SameCodeChoice[] = [];
  /** Lock message of each row by subDocId, "" for the rows that can be ticked */
  lockMessages: { [subDocId: string]: string } = Object.create(null);
  /** Ticked rows by subDocId */
  checked: { [subDocId: string]: boolean } = Object.create(null);
  checkedCount = 0;

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<PropertySameCodeEditDialogComponent, SameCodeEditDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: SameCodeEditDialogData,
    private translateService: TranslateService
  ) {}

  /**
   * Init component
   *
   * @since 3.0.0
   */
  ngOnInit(): void {
    const barCode = this.data && typeof this.data.barCode === "string" ? this.data.barCode : "";
    this.barCode = barCode.substring(barCode.length - 7);
    this.choices = this.data && Array.isArray(this.data.choices) ? this.data.choices : [];
    this.choices.forEach((choice) => {
      this.lockMessages[choice.subDocId] = sameCodeLockMessage(choice, (key, params) =>
        this.translateService.instant(key, params)
      );
    });
  }

  /**
   * Handle the checkbox of a row. A locked row never gets ticked, even if the event reaches here.
   *
   * @since 3.0.0
   */
  onToggle(choice: SameCodeChoice, checked: boolean): void {
    if (choice.lockReason !== null) {
      return;
    }
    this.checked[choice.subDocId] = checked;
    this.checkedCount = this.choices.filter((item) => this.checked[item.subDocId]).length;
  }

  /**
   * Close with the ticked rows, in the order of the list
   *
   * @since 3.0.0
   */
  onContinue(): void {
    const selected = this.choices.filter(
      (choice) => choice.lockReason === null && this.checked[choice.subDocId]
    );
    if (!selected.length) {
      return;
    }
    this.dialogRef.close({ selected });
  }

  /**
   * Handle dialog closing: nothing is edited
   *
   * @since 3.0.0
   */
  onClose(): void {
    this.dialogRef.close();
  }

  trackBySubDocId(index: number, choice: SameCodeChoice): string {
    return choice.subDocId;
  }
}
